/**
 * Utilidades para el servidor de archivos
 * Maneja las URLs de imágenes y archivos estáticos
 */

// URL base del servidor de archivos
const FILE_SERVER_URL = process.env.REACT_APP_FILE_SERVER_URL || 'https://archivos.laptops.pe';

/**
 * Construye la URL completa para una imagen de producto
 * Compatible con plataforma.laptops.pe - mantiene URLs relativas intactas
 * @param {string} imagePath - Ruta de la imagen desde la base de datos
 * @returns {string} URL completa del servidor de archivos o URL original
 */
export function buildImageUrl(imagePath) {
    if (!imagePath) {
        return '/productos/placeholder.png'; // Imagen por defecto local
    }
    
    // Si ya es una URL completa, usarla directamente
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
        return imagePath;
    }
    
    // Si la URL es relativa (/productos/...), construir URL completa del servidor de archivos
    if (imagePath.startsWith('/productos/')) {
        const fullUrl = `${FILE_SERVER_URL}${imagePath}`;
        return fullUrl;
    }
    
    // Para cualquier otra ruta del endpoint, simplemente concatenar con el servidor de archivos
    const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
    const fullUrl = `${FILE_SERVER_URL}${cleanPath}`;
    return fullUrl;
}

/**
 * Construye la URL para una imagen de producto específica
 * @param {number|string} productoId - ID del producto
 * @param {string} imageName - Nombre del archivo de imagen
 * @returns {string} URL completa de la imagen
 */
export function buildProductImageUrl(productoId, imageName) {
    if (!productoId || !imageName) {
        return '/productos/default.jpg';
    }
    
    return `${FILE_SERVER_URL}/productos/${productoId}/${imageName}`;
}

/**
 * Optimiza la URL de imagen para diferentes tamaños
 * @param {string} imageUrl - URL original de la imagen
 * @param {string} size - Tamaño deseado ('thumb', 'medium', 'large')
 * @returns {string} URL optimizada
 */
export function optimizeImageUrl(imageUrl, size = 'medium') {
    if (!imageUrl || imageUrl.startsWith('/')) {
        return imageUrl; // No optimizar imágenes locales
    }
    
    // Si el servidor de archivos soporta redimensionamiento automático
    const sizeParams = {
        thumb: '?w=150&h=150&fit=cover',
        medium: '?w=400&h=400&fit=cover',
        large: '?w=800&h=800&fit=contain'
    };
    
    return imageUrl + (sizeParams[size] || sizeParams.medium);
}

/**
 * Verifica si una URL de imagen es válida y accesible
 * @param {string} imageUrl - URL de la imagen a verificar
 * @returns {Promise<boolean>} True si la imagen es accesible
 */
export async function isImageAccessible(imageUrl) {
    try {
        const response = await fetch(imageUrl, { method: 'HEAD' });
        return response.ok;
    } catch (error) {
        console.warn('Error verificando accesibilidad de imagen:', imageUrl, error);
        return false;
    }
}

/**
 * Obtiene la URL de imagen con fallback
 * @param {string} primaryUrl - URL principal
 * @param {string} fallbackUrl - URL de respaldo
 * @returns {string} URL a usar
 */
export function getImageUrlWithFallback(primaryUrl, fallbackUrl = '/productos/default.jpg') {
    return primaryUrl || '/productos/placeholder.png';
}
